import { Directionality } from '@angular/cdk/bidi';
import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Language } from 'app/core/models/language.model';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/he';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  public languagesList = signal([] as Language[]);
  public currentLanguage = signal({} as Language);
  public direction = signal('ltr');

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    const dir = inject(Directionality, { optional: true });
    if (dir) {
      effect(
        () => {
          const language = this.currentLanguage();
          if (language) {
            (dir as any).value = language.direction;
            dir.change.emit(language.direction);
            this.direction.set(language.direction);
          }
        },
        { allowSignalWrites: true }
      );
    }
  }

  #translate = inject(TranslateService);

  public init(languages: Language[], lang?: string) {
    this.languagesList.set(languages);
    this.#translate.addLangs(languages.map((l) => l.name));
    this.setDefaultLanguage(languages, lang);
  }

  private setDefaultLanguage(languages: Language[], lang?: string) {
    var currentLang = languages.find((l) =>
      lang ? l.prefix == lang : l.isoCode == environment.languageConfiguration.defaultLanguageIsoCode
    );
    if (!currentLang) {
      currentLang = languages.find((l) => l.isoCode == environment.languageConfiguration.defaultLanguageIsoCode);
    }
    if (currentLang) {
      this.currentLanguage.set({ ...currentLang });
      this.setLanguage(currentLang);
    }
  }

  public setLanguage(language: Language) {
    if (language.isoCode != this.currentLanguage().isoCode) {
      let currentUrl = this.router.url.split('?')[0].split('#')[0];
      let hasLanguageOnUrl = currentUrl.split('/')?.[1] == this.currentLanguage().prefix;
      if (hasLanguageOnUrl) {
        currentUrl = currentUrl.replace(`/${this.currentLanguage().prefix}`, '');
      }
      const newUrl = `${language.prefix != 'he' ? language.prefix : ''}${currentUrl}`;
      if(environment.isProd && (language.isoCode === "Ru" || language.isoCode === "Ar")){
        const oldSiteUrl = language.isoCode === "Ar" ? environment.oldSiteAr : environment.oldSiteRu;
        window.location.href = oldSiteUrl;
        return;
    }
      this.currentLanguage.set({ ...language });
      this.router.navigate([newUrl]);
    }
    this.#translate.use(language.isoCode);
    dayjs.locale(language.isoCode);
    document.dir = language.direction;
    this.direction.set(language.direction);
  }
}

export function translateLoaderFunc(http: HttpClient) {
  return new TranslateHttpLoader(http, environment.languageConfiguration.languageStorageUrl + '/assets/i18n/', '.json');
}
